<template>
  <div class="nachrichten container py-5">
    <header class="page-head">
      <div>
        <h1 v-tx="'NachrichtenTitle'">{{ t('title') }}</h1>
        <p class="sub" v-tx="'NachrichtenSubtitle'">{{ t('subtitle') }}</p>
      </div>
      <button type="button" class="new-btn" @click="openNew" v-tx="'NachrichtenNewTicket'">{{ t('newTicket') }}</button>
    </header>

    <div class="layout">
      <!-- SOL: ticket siyahısı -->
      <aside class="col-list">
        <input class="search" type="text" v-model="search" :placeholder="t('search')" />
        <div class="tabs">
          <button v-for="tb in tabs" :key="tb.key" :class="{ active: tab === tb.key }" @click="setTab(tb.key)" v-tx="tb.txKey">
            {{ tb.label }} ({{ tb.count }})
          </button>
        </div>

        <div class="list" v-if="filteredTickets.length">
          <button
            v-for="tk in filteredTickets"
            :key="tk.id"
            class="ticket"
            :class="{ active: selectedId === tk.id }"
            @click="select(tk.id)"
          >
            <div class="ticket__top">
              <span class="ticket__num">#{{ tk.id }}</span>
              <span class="ticket__time">{{ relativeTime(tk.lastMessageDate) }}</span>
            </div>
            <div class="ticket__row">
              <span class="avatar" :style="{ background: avatarColor(tk.nurseProfileId) }">{{ initials(tk.nurseFullName) }}</span>
              <span class="ticket__name">{{ tk.nurseFullName || tk.subject }}</span>
            </div>
            <div class="ticket__row2">
              <span class="tag" :style="tagStyle(typeColor(tk.typeCode))">{{ typeLabel(tk.typeCode) }}</span>
              <span class="ticket__subject">{{ tk.subject }}</span>
              <span class="status" :style="tagStyle(statusColor(tk.statusCode))">{{ statusLabel(tk.statusCode) }}</span>
            </div>
          </button>
        </div>
        <div class="empty" v-else v-tx="'NachrichtenEmptyList'">{{ t('emptyList') }}</div>
      </aside>

      <!-- ORTA: söhbət -->
      <section class="col-thread">
        <template v-if="detail">
          <header class="thread-head">
            <span class="avatar iga">IGA</span>
            <div class="thread-head__main">
              <strong v-tx="'NachrichtenSupportName'">{{ t('supportName') }}</strong>
              <span class="thread-head__sub">#{{ detail.id }}<template v-if="detail.nurseFullName"> · {{ detail.nurseFullName }}</template></span>
            </div>
            <span class="no-direct" v-tx="'NachrichtenNoDirect'">{{ t('noDirect') }}</span>
          </header>

          <div class="messages" ref="messages">
            <div v-for="m in detail.messages" :key="m.id" class="msg" :class="m.senderCode === 'Clinic' ? 'mine' : 'iga'">
              <div class="bubble">{{ m.body }}</div>
              <div class="meta">
                <span v-if="m.senderCode === 'Igaplus'" class="confirmed" v-tx="'NachrichtenFromIga'">{{ t('fromIga') }}</span>
                <span class="time">{{ timeOnly(m.createdDate) }}</span>
              </div>
            </div>
          </div>

          <form class="composer" @submit.prevent="send">
            <input type="text" v-model="draft" :placeholder="t('composer')" :disabled="detail.statusCode === 'Erledigt' || sending" />
            <button type="submit" class="send" :disabled="!draft.trim() || sending || detail.statusCode === 'Erledigt'" v-tx="'NachrichtenSend'">{{ t('send') }}</button>
          </form>
        </template>
        <div v-else class="thread-empty" v-tx="'NachrichtenThreadEmpty'">{{ t('threadEmpty') }}</div>
      </section>

      <!-- SAĞ: detal -->
      <aside class="col-details" v-if="detail">
        <h3 v-tx="'NachrichtenDetailsTitle'">{{ t('detailsTitle') }}</h3>
        <div class="d-row"><span class="k" v-tx="'NachrichtenDId'">{{ t('dId') }}</span><span class="v">#{{ detail.id }}</span></div>
        <div class="d-row"><span class="k" v-tx="'NachrichtenDStatus'">{{ t('dStatus') }}</span><span class="v">{{ statusLabel(detail.statusCode) }}</span></div>
        <div class="d-row"><span class="k" v-tx="'NachrichtenDBewerber'">{{ t('dBewerber') }}</span><span class="v">{{ detail.nurseFullName || '—' }}</span></div>
        <div class="d-row"><span class="k" v-tx="'NachrichtenDErstellt'">{{ t('dErstellt') }}</span><span class="v">{{ dateOnly(detail.createdDate) }}</span></div>
        <div class="d-row"><span class="k" v-tx="'NachrichtenDPriority'">{{ t('dPriority') }}</span><span class="v" :style="{ color: priorityColor(detail.priorityCode) }">{{ priorityLabel(detail.priorityCode) }}</span></div>
        <div class="d-row"><span class="k" v-tx="'NachrichtenDDepartment'">{{ t('dDepartment') }}</span><span class="v">{{ detail.department || '—' }}</span></div>
        <div class="d-row"><span class="k" v-tx="'NachrichtenDWorkflow'">{{ t('dWorkflow') }}</span><span class="v">{{ detail.workflow || '—' }}</span></div>

        <button v-if="detail.statusCode !== 'Erledigt'" type="button" class="close-btn" :disabled="closing" @click="closeTicket" v-tx="'NachrichtenCloseTicket'">
          {{ closing ? '…' : t('closeTicket') }}
        </button>
      </aside>
    </div>

    <!-- Yeni ticket modalı (body-yə portal olunur) -->
    <div v-if="newOpen" ref="modalRoot" class="nachrichten-modal-backdrop" @click="closeNew">
      <div class="nm-modal" @click.stop>
        <header class="nm-head">
          <h2 v-tx="'NachrichtenModalTitle'">{{ t('modalTitle') }}</h2>
          <button type="button" class="x" @click="closeNew">✕</button>
        </header>
        <div class="nm-body">
          <label class="fld"><span v-tx="'NachrichtenFSubject'">{{ t('fSubject') }} *</span>
            <input type="text" v-model="form.subject" maxlength="300" />
          </label>
          <div class="fld-row">
            <label class="fld"><span v-tx="'NachrichtenFType'">{{ t('fType') }}</span>
              <select v-model.number="form.type">
                <option v-for="o in typeOptions" :key="o.value" :value="o.value">{{ typeLabel(o.code) }}</option>
              </select>
            </label>
            <label class="fld"><span v-tx="'NachrichtenDPriority'">{{ t('dPriority') }}</span>
              <select v-model.number="form.priority">
                <option v-for="o in priorityOptions" :key="o.value" :value="o.value">{{ priorityLabel(o.code) }}</option>
              </select>
            </label>
          </div>
          <label class="fld"><span v-tx="'NachrichtenFBewerber'">{{ t('fBewerber') }}</span>
            <select v-model="form.nurseProfileId">
              <option :value="null">—</option>
              <option v-for="n in nurseOptions" :key="n.id" :value="n.id">{{ n.name }}</option>
            </select>
          </label>
          <label class="fld"><span v-tx="'NachrichtenFDepartment'">{{ t('fDepartment') }}</span>
            <input type="text" v-model="form.department" maxlength="150" :placeholder="t('departmentPh')" />
          </label>
          <label class="fld"><span v-tx="'NachrichtenFMessage'">{{ t('fMessage') }} *</span>
            <textarea v-model="form.message" rows="4" maxlength="4000"></textarea>
          </label>
        </div>
        <footer class="nm-foot">
          <button type="button" class="ghost" @click="closeNew" :disabled="creating" v-tx="'NachrichtenCancel'">{{ t('cancel') }}</button>
          <button type="button" class="primary" @click="create" :disabled="creating || !form.subject.trim() || !form.message.trim()" v-tx="'NachrichtenCreateBtn'">
            {{ creating ? t('creating') : t('createBtn') }}
          </button>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";

const PALETTE = ["#132B59", "#894FE5", "#0FA5C6", "#10B881", "#F39C12", "#EF4444"];
const LOCALES = { az: "az-AZ", de: "de-DE", en: "en-US", ru: "ru-RU", tr: "tr-TR" };

const TYPE_COLOR = { Anfrage: "#294C8E", Rueckfrage: "#894FE5", Info: "#0FA5C6" };
const STATUS_COLOR = { Offen: "#F39C12", Erledigt: "#10B881" };
const PRIORITY_COLOR = { Niedrig: "#9EA6B6", Mittel: "#F39C12", Hoch: "#EF4444" };

const T = {
  az: {
    title: "Mesajlar", subtitle: "IGAplus üzərindən ünsiyyət — namizədlərlə birbaşa əlaqə yoxdur.",
    newTicket: "+ Yeni müraciət", search: "Müraciət axtar…",
    tabAlle: "Hamısı", tabOffen: "Açıq", tabErledigt: "Bağlı",
    emptyList: "Heç bir müraciət yoxdur.", threadEmpty: "Müraciət seçin və ya yeni müraciət yaradın.",
    supportName: "IGAplus – Dəstək", noDirect: "Namizədlərlə birbaşa əlaqə yoxdur",
    fromIga: "IGAplus-dan", composer: "IGAplus-a yazın…", send: "Göndər",
    detailsTitle: "Müraciət detalları", dId: "Müraciət ID", dStatus: "Status", dBewerber: "Namizəd",
    dErstellt: "Yaradılıb", dPriority: "Prioritet", dDepartment: "Şöbə", dWorkflow: "Mərhələ",
    closeTicket: "Müraciəti bağla",
    modalTitle: "Yeni müraciət", fSubject: "Mövzu", fType: "Tip", fBewerber: "Namizəd (opsional)",
    fDepartment: "Şöbə (opsional)", departmentPh: "məs. Station B3", fMessage: "Mesaj",
    cancel: "Ləğv et", createBtn: "Müraciət yarat", creating: "Göndərilir…", createdToast: "Müraciət yaradıldı",
    type_Anfrage: "Sorğu", type_Rueckfrage: "Əlavə sual", type_Info: "Məlumat",
    status_Offen: "Açıq", status_Erledigt: "Bağlı",
    prio_Niedrig: "Aşağı", prio_Mittel: "Orta", prio_Hoch: "Yüksək",
  },
  de: {
    title: "Nachrichten", subtitle: "Kommunikation über IGAplus – kein Direktkontakt mit Bewerbern.",
    newTicket: "+ Neues Ticket", search: "Ticket suchen…",
    tabAlle: "Alle", tabOffen: "Offen", tabErledigt: "Erledigt",
    emptyList: "Keine Tickets vorhanden.", threadEmpty: "Wählen Sie ein Ticket oder erstellen Sie ein neues.",
    supportName: "IGAplus – Support", noDirect: "Kein Direktkontakt mit Bewerbern",
    fromIga: "Von IGAplus", composer: "An IGAplus schreiben…", send: "Absenden",
    detailsTitle: "Ticket-Details", dId: "Ticket-ID", dStatus: "Status", dBewerber: "Bewerber",
    dErstellt: "Erstellt", dPriority: "Priorität", dDepartment: "Abteilung", dWorkflow: "Workflow",
    closeTicket: "Ticket schließen",
    modalTitle: "Neues Ticket", fSubject: "Betreff", fType: "Typ", fBewerber: "Bewerber (optional)",
    fDepartment: "Abteilung (optional)", departmentPh: "z. B. Station B3", fMessage: "Nachricht",
    cancel: "Abbrechen", createBtn: "Ticket erstellen", creating: "Wird gesendet…", createdToast: "Ticket erstellt",
    type_Anfrage: "Anfrage", type_Rueckfrage: "Rückfrage", type_Info: "Info",
    status_Offen: "Offen", status_Erledigt: "Erledigt",
    prio_Niedrig: "Niedrig", prio_Mittel: "Mittel", prio_Hoch: "Hoch",
  },
  en: {
    title: "Messages", subtitle: "Communication via IGAplus – no direct contact with candidates.",
    newTicket: "+ New ticket", search: "Search ticket…",
    tabAlle: "All", tabOffen: "Open", tabErledigt: "Done",
    emptyList: "No tickets.", threadEmpty: "Select a ticket or create a new one.",
    supportName: "IGAplus – Support", noDirect: "No direct contact with candidates",
    fromIga: "From IGAplus", composer: "Write to IGAplus…", send: "Send",
    detailsTitle: "Ticket details", dId: "Ticket ID", dStatus: "Status", dBewerber: "Candidate",
    dErstellt: "Created", dPriority: "Priority", dDepartment: "Department", dWorkflow: "Workflow",
    closeTicket: "Close ticket",
    modalTitle: "New ticket", fSubject: "Subject", fType: "Type", fBewerber: "Candidate (optional)",
    fDepartment: "Department (optional)", departmentPh: "e.g. Station B3", fMessage: "Message",
    cancel: "Cancel", createBtn: "Create ticket", creating: "Sending…", createdToast: "Ticket created",
    type_Anfrage: "Request", type_Rueckfrage: "Follow-up", type_Info: "Info",
    status_Offen: "Open", status_Erledigt: "Done",
    prio_Niedrig: "Low", prio_Mittel: "Medium", prio_Hoch: "High",
  },
  ru: {
    title: "Сообщения", subtitle: "Общение через IGAplus — без прямого контакта с кандидатами.",
    newTicket: "+ Новый тикет", search: "Поиск тикета…",
    tabAlle: "Все", tabOffen: "Открытые", tabErledigt: "Закрытые",
    emptyList: "Тикетов нет.", threadEmpty: "Выберите тикет или создайте новый.",
    supportName: "IGAplus – Поддержка", noDirect: "Без прямого контакта с кандидатами",
    fromIga: "От IGAplus", composer: "Написать в IGAplus…", send: "Отправить",
    detailsTitle: "Детали тикета", dId: "ID тикета", dStatus: "Статус", dBewerber: "Кандидат",
    dErstellt: "Создан", dPriority: "Приоритет", dDepartment: "Отдел", dWorkflow: "Этап",
    closeTicket: "Закрыть тикет",
    modalTitle: "Новый тикет", fSubject: "Тема", fType: "Тип", fBewerber: "Кандидат (необяз.)",
    fDepartment: "Отдел (необяз.)", departmentPh: "напр. Station B3", fMessage: "Сообщение",
    cancel: "Отмена", createBtn: "Создать тикет", creating: "Отправка…", createdToast: "Тикет создан",
    type_Anfrage: "Запрос", type_Rueckfrage: "Доп. вопрос", type_Info: "Инфо",
    status_Offen: "Открыт", status_Erledigt: "Закрыт",
    prio_Niedrig: "Низкий", prio_Mittel: "Средний", prio_Hoch: "Высокий",
  },
  tr: {
    title: "Mesajlar", subtitle: "IGAplus üzerinden iletişim – adaylarla doğrudan temas yok.",
    newTicket: "+ Yeni talep", search: "Talep ara…",
    tabAlle: "Tümü", tabOffen: "Açık", tabErledigt: "Kapalı",
    emptyList: "Talep yok.", threadEmpty: "Bir talep seçin veya yeni oluşturun.",
    supportName: "IGAplus – Destek", noDirect: "Adaylarla doğrudan temas yok",
    fromIga: "IGAplus'tan", composer: "IGAplus'a yazın…", send: "Gönder",
    detailsTitle: "Talep detayları", dId: "Talep ID", dStatus: "Durum", dBewerber: "Aday",
    dErstellt: "Oluşturuldu", dPriority: "Öncelik", dDepartment: "Departman", dWorkflow: "Aşama",
    closeTicket: "Talebi kapat",
    modalTitle: "Yeni talep", fSubject: "Konu", fType: "Tür", fBewerber: "Aday (opsiyonel)",
    fDepartment: "Departman (opsiyonel)", departmentPh: "örn. Station B3", fMessage: "Mesaj",
    cancel: "İptal", createBtn: "Talep oluştur", creating: "Gönderiliyor…", createdToast: "Talep oluşturuldu",
    type_Anfrage: "Talep", type_Rueckfrage: "Geri soru", type_Info: "Bilgi",
    status_Offen: "Açık", status_Erledigt: "Kapalı",
    prio_Niedrig: "Düşük", prio_Mittel: "Orta", prio_Hoch: "Yüksek",
  },
};

export default {
  name: "ClinicNachrichtenPage",
  data() {
    return {
      tickets: [],
      counts: { all: 0, open: 0, done: 0 },
      tab: "Offen",
      search: "",
      selectedId: null,
      detail: null,
      draft: "",
      sending: false,
      closing: false,
      newOpen: false,
      creating: false,
      nurseOptions: [],
      form: { subject: "", type: 0, priority: 1, nurseProfileId: null, department: "", message: "" },
      typeOptions: [
        { value: 0, code: "Anfrage" }, { value: 1, code: "Rueckfrage" }, { value: 2, code: "Info" },
      ],
      priorityOptions: [
        { value: 0, code: "Niedrig" }, { value: 1, code: "Mittel" }, { value: 2, code: "Hoch" },
      ],
    };
  },
  computed: {
    lang() { return this.$store.state.lang || "az"; },
    tabs() {
      return [
        { key: "Alle", txKey: "NachrichtenTabAlle", label: this.t("tabAlle"), count: this.counts.all },
        { key: "Offen", txKey: "NachrichtenTabOffen", label: this.t("tabOffen"), count: this.counts.open },
        { key: "Erledigt", txKey: "NachrichtenTabErledigt", label: this.t("tabErledigt"), count: this.counts.done },
      ];
    },
    filteredTickets() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.tickets;
      return this.tickets.filter((tk) =>
        (tk.subject || "").toLowerCase().includes(q) ||
        (tk.nurseFullName || "").toLowerCase().includes(q) ||
        String(tk.id).includes(q));
    },
  },
  watch: {
    newOpen(v) {
      if (v) {
        // animate.css keçidi səbəbindən valideyn transform yaradır →
        // position:fixed pozulur. Modalı body-yə köçürürük.
        this.$nextTick(() => {
          const el = this.$refs.modalRoot;
          if (el && el.parentNode !== document.body) document.body.appendChild(el);
        });
      }
    },
  },
  mounted() {
    this.loadList();
    this.loadNurses();
  },
  beforeDestroy() {
    const el = this.$refs.modalRoot;
    if (el && el.parentNode) el.parentNode.removeChild(el);
  },
  methods: {
    t(key) {
      const sKey = "Nachrichten" + key.charAt(0).toUpperCase() + key.slice(1);
      const ov = this.$store.state.statics && this.$store.state.statics[sKey];
      if (ov && String(ov).trim()) return ov;
      const dict = T[this.lang] || T.az;
      return dict[key] != null ? dict[key] : (T.az[key] || key);
    },
    async loadList(keepSelection) {
      try {
        const status = this.tab === "Alle" ? "" : this.tab;
        const res = await axios.get(`/clinic/tickets`, { params: { status } });
        const d = res.data?.data || {};
        this.tickets = d.items || [];
        this.counts = { all: d.countAll || 0, open: d.countOpen || 0, done: d.countDone || 0 };
        if (!keepSelection && this.tickets.length && !this.tickets.find((tk) => tk.id === this.selectedId)) {
          this.select(this.tickets[0].id);
        }
      } catch { /* toast */ }
    },
    async loadNurses() {
      try {
        const res = await axios.get(`/clinic/applications`);
        const data = res.data?.data;
        const items = Array.isArray(data) ? data : (data?.items || []);
        const seen = {};
        this.nurseOptions = items
          .filter((a) => a.nurseProfileId && !seen[a.nurseProfileId] && (seen[a.nurseProfileId] = true))
          .map((a) => ({ id: a.nurseProfileId, name: a.nurseFullName }));
      } catch { this.nurseOptions = []; }
    },
    setTab(key) { this.tab = key; this.loadList(); },
    async select(id) {
      this.selectedId = id;
      try {
        const res = await axios.get(`/clinic/tickets/${id}`);
        this.detail = res.data?.data || null;
        this.$nextTick(this.scrollMessages);
      } catch { this.detail = null; }
    },
    async send() {
      const body = this.draft.trim();
      if (!body) return;
      this.sending = true;
      try {
        await axios.post(`/clinic/tickets/${this.selectedId}/messages`, { body });
        this.draft = "";
        await this.select(this.selectedId);
        this.loadList(true);
      } catch { /* toast */ } finally { this.sending = false; }
    },
    async closeTicket() {
      this.closing = true;
      try {
        await axios.post(`/clinic/tickets/${this.selectedId}/close`);
        await this.select(this.selectedId);
        this.loadList(true);
      } catch { /* toast */ } finally { this.closing = false; }
    },
    openNew() {
      this.form = { subject: "", type: 0, priority: 1, nurseProfileId: null, department: "", message: "" };
      this.newOpen = true;
    },
    closeNew() { if (!this.creating) this.newOpen = false; },
    async create() {
      if (!this.form.subject.trim() || !this.form.message.trim()) return;
      this.creating = true;
      try {
        const res = await axios.post(`/clinic/tickets`, this.form);
        const newId = res.data?.data;
        this.newOpen = false;
        await this.loadList(true);
        if (newId) this.select(newId);
        if (this.$toast) this.$toast.success(this.t("createdToast"));
      } catch { /* toast */ } finally { this.creating = false; }
    },
    scrollMessages() {
      const el = this.$refs.messages;
      if (el) el.scrollTop = el.scrollHeight;
    },
    // helpers
    avatarColor(id) { return PALETTE[(Number(id) || 0) % PALETTE.length]; },
    initials(name) {
      const parts = (name || "").trim().split(/\s+/).filter((p) => /^[\p{L}]/u.test(p));
      return ((parts[0] || "").charAt(0) + (parts[1] || "").charAt(0)).toUpperCase() || "?";
    },
    typeLabel(c) { return this.t("type_" + c); },
    typeColor(c) { return TYPE_COLOR[c] || "#9EA6B6"; },
    statusLabel(c) { return this.t("status_" + c); },
    statusColor(c) { return STATUS_COLOR[c] || "#9EA6B6"; },
    priorityLabel(c) { return this.t("prio_" + c); },
    priorityColor(c) { return PRIORITY_COLOR[c] || "#9EA6B6"; },
    tagStyle(color) { return { color, background: color + "1F" }; },
    relativeTime(d) {
      if (!d) return "";
      const min = Math.floor((Date.now() - new Date(d).getTime()) / 60000);
      const u = { az: ["indi", "dəq", "s", "Dünən", "g"], de: ["jetzt", "Min", "Std", "Gestern", "T"], en: ["now", "m", "h", "Yesterday", "d"], ru: ["сейчас", "м", "ч", "Вчера", "д"], tr: ["şimdi", "dk", "s", "Dün", "g"] }[this.lang] || ["indi", "dəq", "s", "Dünən", "g"];
      if (min < 1) return u[0];
      if (min < 60) return `${min} ${u[1]}`;
      const h = Math.floor(min / 60);
      if (h < 24) return `${h} ${u[2]}`;
      const days = Math.floor(h / 24);
      if (days === 1) return u[3];
      if (days < 7) return `${days} ${u[4]}`;
      try { return new Date(d).toLocaleDateString(LOCALES[this.lang] || "az-AZ", { day: "2-digit", month: "2-digit" }); } catch { return ""; }
    },
    timeOnly(d) { try { return new Date(d).toLocaleTimeString(LOCALES[this.lang] || "az-AZ", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } },
    dateOnly(d) { try { return new Date(d).toLocaleDateString(LOCALES[this.lang] || "az-AZ", { day: "2-digit", month: "2-digit", year: "numeric" }); } catch { return ""; } },
  },
};
</script>

<style lang="scss" scoped>
.nachrichten { max-width: 1180px; font-family: "Roboto", "Segoe UI", Arial, sans-serif; }

.page-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 18px;
  h1 { margin: 0 0 6px; font-size: 22px; font-weight: 700; color: #132B59; line-height: 1; }
  .sub { margin: 0; font-size: 13px; color: #666F82; }
  .new-btn { flex-shrink: 0; height: 40px; padding: 0 16px; border: 0; border-radius: 8px; background: #132B59; color: #fff; font: inherit; font-size: 13px; font-weight: 600; cursor: pointer;
    &:hover { background: #0e2147; } }
}

.layout { display: grid; grid-template-columns: 320px 1fr 280px; gap: 16px; align-items: start;
  @media (max-width: 1100px) { grid-template-columns: 280px 1fr; .col-details { display: none; } }
  @media (max-width: 767px) { grid-template-columns: 1fr; }
}

%card { background: #fff; border: 1px solid #E0E3EA; border-radius: 12px; }

.col-list { @extend %card; padding: 12px; display: flex; flex-direction: column; gap: 10px; max-height: 620px;
  .search { height: 38px; padding: 0 12px; border: 1px solid #E0E3EA; border-radius: 8px; font: inherit; font-size: 13px; outline: none; &:focus { border-color: #132B59; } }
  .tabs { display: flex; gap: 4px;
    button { flex: 1; height: 32px; border: 0; background: #F5F6FA; border-radius: 7px; font: inherit; font-size: 12px; font-weight: 600; color: #666F82; cursor: pointer;
      &.active { background: #132B59; color: #fff; } }
  }
  .list { overflow-y: auto; display: flex; flex-direction: column; gap: 8px; }
  .empty { color: #9EA6B6; font-size: 13px; text-align: center; padding: 20px 0; }
}

.ticket { text-align: left; border: 1px solid #EEF0F4; border-radius: 10px; background: #fff; padding: 10px 12px; cursor: pointer; display: flex; flex-direction: column; gap: 6px;
  &:hover { background: #f8fafc; }
  &.active { border-color: #132B59; box-shadow: 0 0 0 1px #132B59; }
  &__top { display: flex; justify-content: space-between; font-size: 11px; color: #9EA6B6; }
  &__row { display: flex; align-items: center; gap: 8px;
    .avatar { width: 28px; height: 28px; border-radius: 50%; color: #fff; font-size: 10px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; text-transform: uppercase; }
    .ticket__name { font-size: 13px; font-weight: 700; color: #151924; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  }
  &__row2 { display: flex; align-items: center; gap: 6px; min-width: 0;
    .ticket__subject { flex: 1; min-width: 0; font-size: 11px; color: #666F82; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  }
}
.tag, .status { font-size: 10px; font-weight: 600; padding: 2px 7px; border-radius: 10px; white-space: nowrap; }

.col-thread { @extend %card; display: flex; flex-direction: column; height: 620px;
  .thread-head { display: flex; align-items: center; gap: 10px; padding: 14px 16px; border-bottom: 1px solid #EEF0F4;
    .avatar.iga { width: 36px; height: 36px; border-radius: 50%; background: #132B59; color: #fff; font-size: 11px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
    &__main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px;
      strong { font-size: 14px; color: #151924; }
      .thread-head__sub { font-size: 12px; color: #666F82; }
    }
    .no-direct { font-size: 11px; font-weight: 600; color: #F39C12; white-space: nowrap; }
  }
  .messages { flex: 1; overflow-y: auto; padding: 16px; display: flex; flex-direction: column; gap: 12px;
    .msg { display: flex; flex-direction: column; max-width: 72%;
      .bubble { padding: 10px 14px; border-radius: 12px; font-size: 13px; line-height: 1.45; }
      .meta { display: flex; gap: 8px; align-items: center; margin-top: 3px; font-size: 10px; color: #9EA6B6;
        .confirmed { color: #10B881; font-weight: 600; }
      }
      &.iga { align-self: flex-start; .bubble { background: #F1F3F7; color: #1f2733; border-top-left-radius: 4px; } }
      &.mine { align-self: flex-end; align-items: flex-end; .bubble { background: #132B59; color: #fff; border-top-right-radius: 4px; } }
    }
  }
  .composer { display: flex; gap: 8px; padding: 12px 16px; border-top: 1px solid #EEF0F4;
    input { flex: 1; height: 40px; padding: 0 12px; border: 1px solid #E0E3EA; border-radius: 8px; font: inherit; font-size: 13px; outline: none; &:focus { border-color: #132B59; } }
    .send { height: 40px; padding: 0 18px; border: 0; border-radius: 8px; background: #132B59; color: #fff; font: inherit; font-size: 13px; font-weight: 600; cursor: pointer;
      &:hover:not(:disabled) { background: #0e2147; } &:disabled { opacity: 0.5; cursor: not-allowed; } }
  }
  .thread-empty { flex: 1; display: flex; align-items: center; justify-content: center; color: #9EA6B6; font-size: 14px; }
}

.col-details { @extend %card; padding: 16px;
  h3 { margin: 0 0 14px; font-size: 14px; font-weight: 700; color: #151924; padding-bottom: 12px; border-bottom: 1px solid #EEF0F4; }
  .d-row { display: flex; justify-content: space-between; gap: 12px; margin-bottom: 12px;
    .k { font-size: 12px; color: #9EA6B6; }
    .v { font-size: 12px; font-weight: 600; color: #151924; text-align: right; }
  }
  .close-btn { width: 100%; height: 40px; margin-top: 8px; border: 0; border-radius: 8px; background: #10B881; color: #fff; font: inherit; font-size: 13px; font-weight: 600; cursor: pointer;
    &:hover:not(:disabled) { background: #0e9d6e; } &:disabled { opacity: 0.6; } }
}
</style>

<!-- Modal stilləri scoped DEYİL — element body-yə köçürülür, scoped data-atributu ilə uyğunlaşması üçün -->
<style lang="scss">
.nachrichten-modal-backdrop {
  position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
  background: rgba(15, 23, 42, 0.5); z-index: 3000;
  display: flex; align-items: center; justify-content: center; padding: 16px;
  font-family: "Roboto", "Segoe UI", Arial, sans-serif;

  .nm-modal { width: 520px; max-width: 100%; background: #fff; border-radius: 14px; overflow: hidden; display: flex; flex-direction: column; max-height: 90vh; box-shadow: 0 20px 50px rgba(15,23,42,0.3); }
  .nm-head { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; border-bottom: 1px solid #EEF0F4;
    h2 { margin: 0; font-size: 16px; color: #132B59; }
    .x { background: 0; border: 0; font-size: 18px; color: #9EA6B6; cursor: pointer; }
  }
  .nm-body { padding: 18px 20px; overflow-y: auto; display: flex; flex-direction: column; gap: 12px;
    .fld { display: flex; flex-direction: column; gap: 5px;
      span { font-size: 12px; font-weight: 600; color: #475569; }
      input, select, textarea { padding: 9px 11px; border: 1px solid #E0E3EA; border-radius: 8px; font: inherit; font-size: 13px; outline: none; resize: vertical; &:focus { border-color: #132B59; } }
    }
    .fld-row { display: flex; gap: 12px; .fld { flex: 1; } }
  }
  .nm-foot { display: flex; justify-content: flex-end; gap: 8px; padding: 14px 20px; border-top: 1px solid #EEF0F4;
    button { height: 40px; padding: 0 18px; border-radius: 8px; font: inherit; font-size: 13px; font-weight: 600; cursor: pointer; border: 1px solid transparent;
      &.ghost { background: #fff; color: #475569; border-color: #E0E3EA; }
      &.primary { background: #132B59; color: #fff; &:hover:not(:disabled) { background: #0e2147; } &:disabled { opacity: 0.6; } }
    }
  }
}
</style>
